import { Box, Flex, Text, useColorMode } from '@chakra-ui/react';
import React from 'react';

import config from 'configs/app';
import useApiQuery from 'lib/api/useApiQuery';
import { useCoinPrice } from 'lib/contexts/CoinPriceContext';
import useNavItems, { isGroupItem } from 'lib/hooks/useNavItems';
import RewardsButton from 'ui/rewards/RewardsButton';
import IconSvg from 'ui/shared/IconSvg';
import { CONTENT_MAX_WIDTH } from 'ui/shared/layout/utils';
import NetworkLogo from 'ui/snippets/networkMenu/NetworkLogo';
import SearchBar from 'ui/snippets/searchBar/SearchBar';
import UserMenu from 'ui/snippets/user/UserMenu';

import TestnetBadge from '../TestnetBadge';
import NavLink from './NavLink';
import NavLinkGroup from './NavLinkGroup';

interface Props {
  isHomePage?: boolean;
}

const NavigationDesktop = ({ isHomePage = false }: Props) => {
  const { mainNavItems } = useNavItems();
  const { colorMode, toggleColorMode } = useColorMode();
  const [ mounted, setMounted ] = React.useState(false);
  const {
    seedxPrice,
    seedxChangePercent,
    isSeedxPriceLoading,
    usdtPrice,
    formatPrice,
    formatChange,
    isLoading: priceLoading,
  } = useCoinPrice();

  React.useEffect(() => {
    setMounted(true);
  }, []);

  const statsQuery = useApiQuery('stats', {
    queryOptions: {
      refetchOnMount: false,
    },
  });

  const gasData = statsQuery.data?.gas_prices?.average;
  let gasPriceValue = '5';
  if (typeof gasData === 'number') {
    gasPriceValue = String(gasData);
  } else if (gasData && typeof gasData === 'object' && 'price' in gasData) {
    gasPriceValue = String((gasData as { price: number }).price);
  }

  let themeIcon: 'sun' | 'moon' = 'sun';
  if (mounted && colorMode === 'dark') {
    themeIcon = 'moon';
  }

  return (
    <Box
      display={{ base: 'none', lg: 'block' }}
      position="sticky"
      top="0"
      zIndex={ 1019 }
      // boxShadow="0 5px 10px rgba(151,164,175,.05)"
      _dark={{ boxShadow: '0 5px 10px rgba(0,0,0,.2)' }}
      bg="card_bg"
      // borderBottom="1px solid"
      // borderColor="card_border"
    >
      { /* TOP BAR */ }
      <Box
        borderBottom="1px solid"
        borderColor="card_border"
        py="6px"
      >
        <Flex
          alignItems="center"
          justifyContent="space-between"
          px={ 6 }
          maxW={ `${ CONTENT_MAX_WIDTH }px` }
          m="0 auto"
          fontSize="12px"
        >
          { /* Left side - Price & Gas info — only render on client to avoid hydration mismatch */ }
          <Flex alignItems="center" gap={ 3 } flexShrink={ 0 } suppressHydrationWarning>
            { mounted ? (
              <>
                { /* Seedx WDSC/USDT price */ }
                <Flex alignItems="center" gap={ 1 }>
                  <Text color="text_muted" fontSize="12.6px">Seedx price:</Text>
                  <Text color="link_accent" fontWeight={ 500 } fontSize="12.6px" suppressHydrationWarning>
                    { isSeedxPriceLoading ? '...' : formatPrice(seedxPrice) }
                  </Text>
                  { !isSeedxPriceLoading && seedxPrice > 0 && (
                    <Text
                      color={ seedxChangePercent >= 0 ? 'green.500' : 'red.500' }
                      fontSize="12.6px"
                      suppressHydrationWarning
                    >
                      ({ formatChange(seedxChangePercent) })
                    </Text>
                  ) }
                </Flex>

                { /* Separator */ }
                <Box w="1px" h="12px" bg="card_border"/>

                { /* USDT Price from CoinMarketCap */ }
                <Flex alignItems="center" gap={ 1 }>
                  <Text color="text_muted" fontSize="12.6px">USDT:</Text>
                  <Text color="link_accent" fontWeight={ 500 } fontSize="12.6px" suppressHydrationWarning>
                    { priceLoading ? '...' : formatPrice(usdtPrice) }
                  </Text>
                </Flex>

                { /* Separator */ }
                <Box w="1px" h="12px" bg="card_border"/>

                { /* Gas */ }
                <Flex alignItems="center" gap={ 1 }>
                  <IconSvg name="gas" boxSize="12px" color="text_muted"/>
                  <Text color="text_muted" fontSize="12.25px">Gas:</Text>
                  <Text color="link_accent" fontWeight={ 500 } fontSize="12.25px" suppressHydrationWarning>
                    { gasPriceValue } Gwei
                  </Text>
                </Flex>
              </>
            ) : (
              <>
                <Flex alignItems="center" gap={ 1 }>
                  <Text color="text_muted" fontSize="12.6px">Seedx price:</Text>
                  <Text color="link_accent" fontWeight={ 500 } fontSize="12.6px">...</Text>
                </Flex>
                <Box w="1px" h="12px" bg="card_border"/>
                <Flex alignItems="center" gap={ 1 }>
                  <Text color="text_muted" fontSize="12.6px">USDT:</Text>
                  <Text color="link_accent" fontWeight={ 500 } fontSize="12.6px">...</Text>
                </Flex>
                <Box w="1px" h="12px" bg="card_border"/>
                <Flex alignItems="center" gap={ 1 }>
                  <IconSvg name="gas" boxSize="12px" color="text_muted"/>
                  <Text color="text_muted" fontSize="12.25px">Gas:</Text>
                  <Text color="link_accent" fontWeight={ 500 } fontSize="12.25px">... Gwei</Text>
                </Flex>
              </>
            ) }
          </Flex>

          { /* Search Bar (Only on non-home pages) */ }
          { !isHomePage && (
            <Box flexGrow={ 1 } marginLeft="auto" marginRight="12px" maxWidth="550px">
              <SearchBar/>
            </Box>
          ) }

          { /* Right side - Icon buttons */ }
          <Flex alignItems="center" gap="6px" flexShrink={ 0 }>
            { /* View toggle */ }
            <Box
              as="button"
              w="35px"
              h="35px"
              border="1px solid"
              borderColor="button_outline_border"
              borderRadius="6px"
              bg="card_bg"
              display="flex"
              alignItems="center"
              justifyContent="center"
              color="text_muted"
              cursor="pointer"
              _hover={{ borderColor: 'link_accent', color: 'link_accent' }}
            >
              <IconSvg name="docs" boxSize="25px"/>
            </Box>

            { /* Theme toggle */ }
            <Box
              as="button"
              w="35px"
              h="35px"
              border="1px solid"
              borderColor="button_outline_border"
              borderRadius="6px"
              bg="card_bg"
              display="flex"
              alignItems="center"
              justifyContent="center"
              color="text_muted"
              cursor="pointer"
              onClick={ toggleColorMode }
              _hover={{ borderColor: 'link_accent', color: 'link_accent' }}
            >
              <IconSvg name={ themeIcon } boxSize="20px"/>
            </Box>

            { /* Network selector */ }
            <Box
              as="button"
              w="35px"
              h="35px"
              border="1px solid"
              borderColor="button_outline_border"
              borderRadius="6px"
              bg="card_bg"
              display="flex"
              alignItems="center"
              justifyContent="center"
              color="text_muted"
              cursor="pointer"
              _hover={{ borderColor: 'link_accent', color: 'link_accent' }}
            >
              <IconSvg name="networks" boxSize="18px"/>
            </Box>
          </Flex>
        </Flex>
      </Box>

      { /* MAIN NAVIGATION BAR */ }
      <Box
        bg="card_bg"
        borderBottom="1px solid"
        borderColor="card_border"
        h="56px"
      >
        <Flex
          alignItems="center"
          justifyContent="space-between"
          px={ 6 }
          h="100%"
          maxW={ `${ CONTENT_MAX_WIDTH }px` }
          m="0 auto"
        >
          { /* Logo - Left */ }
          <Flex alignItems="center">
            <NetworkLogo isCollapsed={ false } w={{ lg: 'auto' }} maxW="220px"/>
            <TestnetBadge ml={ 3 }/>
          </Flex>

          { /* Navigation Menu + Sign In - Right */ }
          <Flex alignItems="center">
            <nav>
              <Flex as="ul" alignItems="center" gap={ 1 }>
                { mainNavItems.map((item) => {
                  if (isGroupItem(item)) {
                    return <NavLinkGroup key={ item.text } item={ item }/>;
                  } else {
                    return <NavLink key={ item.text } item={ item }/>;
                  }
                }) }
              </Flex>
            </nav>

            { /* Separator */ }
            <Text color="text_muted" mx={ 3 } fontSize="lg">|</Text>

            { /* User Menu / Sign In */ }
            <Flex alignItems="center" gap={ 1.5 }>
              { config.features.rewards.isEnabled && <RewardsButton size="sm"/> }
              <UserMenu/>
            </Flex>
          </Flex>
        </Flex>
      </Box>
    </Box>
  );
};

export default React.memo(NavigationDesktop);
